/**
 * voice-v8 — Werkzeugkarte: Rückfrage der Shorts (`shorts_anlegen`, `shorts_neu`, `shorts_vorlegen`). (22.10.2026, UEBERGABE-6-shorts.md)
 *
 * Die Karte zeigt, was geschehen würde — Marke, Thema, Länge, Stimme, Anmerkung, geschätzte Kosten (bzw. Short und was neu wird,
 * oder dass Martin eine Mail bekommt) — und hat zwei Knöpfe. Der Ja-Knopf schickt den FESTEN Wortlaut `shortsKnopfJa(<Schritt>,
 * <Kennung>)` als Martins Nachricht; nur dieses Knopf-Ja führt aus (tools/shorts.ts `jaPasst`). Alles über `textContent` (wertZeile),
 * nie als Markup — Titel und Fehlersatz stammen aus dem Panel.
 *
 * Kein neuer Schreibweg: gesendet wird über den vorhandenen Rückkanal der Karten (`window.__voiceV8SendFromCard`), wie die
 * Trainingskarte (karten/training.ts), mit demselben Knopfpaar (karten/knopf-paar.ts).
 */
import { istShortsSchritt, SHORTS_KARTEN_TITEL, shortsKnopfJa, shortsKnopfNein, VORSCHLAG_KENNUNG } from "../../../shared/shorts.js";
import { absatz, baueKarte, kartenTitel, wertZeile } from "../karte.js";
import { trimText, unwrapResult } from "./hilfe.js";
import { fristAus, haengeKnopfPaarAn } from "./knopf-paar.js";

const text = (v: unknown, max: number): string => (typeof v === "string" ? trimText(v.trim(), max) : "");

/** Der Satz, wenn der Vorschlag abgelaufen ist (nur, wenn das Ergebnis eine Frist `gueltig_bis_ms` trägt) — die Knöpfe sind dann gesperrt. */
export const SHORTS_ABGELAUFEN_SATZ = "Diese Rückfrage ist abgelaufen. Sag Vespera, wenn du den Short noch willst.";

/**
 * Ist dieses Ergebnis eine offene Shorts-Rückfrage mit Ja-Knopf (22.10.2026, Martin 15:08: „Da ist kein Button zum Anklicken!“)? Dann
 * gehört es als KARTE in den Verlauf, nicht als Zeile „wartet auf dein Ja“ in die Schritte (karten/knopf-rueckfrage.ts). Dieselbe
 * Bedingung, unter der `buildShortsVorschlagCard` eine Karte baut — und der Schritt muss zum Werkzeug passen (`shorts_neu` → „neu“).
 */
export function istShortsRueckfrage(tool: string, result: unknown): boolean {
  const schritt = tool.startsWith("shorts_") ? tool.slice("shorts_".length) : "";
  if (!istShortsSchritt(schritt)) return false;
  const r = unwrapResult(result);
  return r.wartet_auf_bestaetigung === true && r.schritt === schritt && typeof r.vorschlag_id === "string" && VORSCHLAG_KENNUNG.test(r.vorschlag_id);
}

/** Die Rückfrage-Karte — `null`, wenn das Ergebnis keinen Vorschlag mit gültiger Kennung und bekanntem Schritt trägt. */
export function buildShortsVorschlagCard(result: unknown): HTMLElement | null {
  const r = unwrapResult(result) as { vorschlag_id?: unknown; schritt?: unknown; zeilen?: unknown; frage?: unknown; gueltig_bis_ms?: unknown };
  const id = typeof r.vorschlag_id === "string" ? r.vorschlag_id : "";
  const schritt = r.schritt;
  if (!VORSCHLAG_KENNUNG.test(id) || !istShortsSchritt(schritt)) return null;
  const el = baueKarte("shorts");
  el.appendChild(kartenTitel(SHORTS_KARTEN_TITEL[schritt]));
  const zeilen = Array.isArray(r.zeilen) ? r.zeilen.slice(0, 10) : [];
  for (const z of zeilen) {
    const o = z && typeof z === "object" ? (z as { feld?: unknown; wert?: unknown }) : {};
    const feld = text(o.feld, 40);
    const wert = text(o.wert, 600);
    if (feld && wert) wertZeile(el, feld, wert);
  }
  el.appendChild(absatz(text(r.frage, 200) || SHORTS_KARTEN_TITEL[schritt]));
  haengeKnopfPaarAn(el, {
    ja: { nachricht: shortsKnopfJa(schritt, id), name: `Ja — ${SHORTS_KARTEN_TITEL[schritt]}`, gesendet: "Gesendet." },
    nein: { nachricht: shortsKnopfNein(schritt, id), name: `Nein — ${SHORTS_KARTEN_TITEL[schritt]}`, gesendet: "Gesendet — es bleibt, wie es ist." },
    bisMs: fristAus(r.gueltig_bis_ms),
    abgelaufenSatz: SHORTS_ABGELAUFEN_SATZ,
    vorschlagId: id,
  });
  return el;
}
